import React, { useState, useEffect } from "react";
import "./HomeChefPricing.css";
import { Info } from "lucide-react";

const HomeChefPricing = ({ cycles, plans, allFeatures, isLoading }) => {
  const [activeCycleKey, setActiveCycleKey] = useState(null);

  // Set default active cycle when data loads
  useEffect(() => {
    if (cycles && cycles.length > 0 && !activeCycleKey) {
      setActiveCycleKey(cycles[0].cycleKey);
    }
  }, [cycles, activeCycleKey]);

  if (isLoading) {
    return (
      <section className="hcp-pricing">
        <div className="container">
          <div className="hcp-pricing__loading">Loading culinary plans...</div>
        </div>
      </section>
    );
  }

  if (!cycles || !plans || !allFeatures) {
    return null;
  }

  // Helper to get price for a specific plan and cycle
  const getPriceForPlan = (plan, cycleKey) => {
    const pricingInfo = plan.pricing.find((p) => p.cycleKey === cycleKey);
    return pricingInfo
      ? pricingInfo.discountedPrice || pricingInfo.subPrice
      : 0;
  };

  // Checks displayLabel first, then falls back to displayName
  const getFeatureText = (featureObj) => {
    if (featureObj.displayLabel) {
      return featureObj.displayLabel;
    }
    if (featureObj.displayName) {
      return featureObj.displayName;
    }
    return "Feature";
  };

  // A simple component to show the tooltip
  const FeatureTooltip = ({ details }) => {
    // If there are no details, don't show anything
    if (!details) return null;

    const items = details
      .split("~")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return (
      <div className="hcp-tooltip-wrapper">
        {/* The Info Icon */}
        <Info size={16} className="hcp-info-icon" />

        {/* The Text that appears on hover */}
        {/* <div className="hcp-tooltip-text">{details}</div> */}

        <ul className="hcp-tooltip-text">
          {items.map((item, index) => (
            <li key={index}>• {item}</li>
          ))}
        </ul>
      </div>
    );
  };

  // Helper to render additional feature details as a bulleted list
  const renderAdditionalFeatureList = (details) => {
    if (!details) return null;

    const items = details
      .split("~")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return (
      <ul className="hcp-feature-sublist">
        {items.map((item, index) => (
          <li key={index}>• {item}</li>
        ))}
      </ul>
    );
  };

  return (
    <section className="hcp-pricing" id="pricing">
      <div className="container">
        {/* Section Header */}
        <div className="hcp-header-wrapper text-center">
          <span className="hcp-overline">INVESTMENT TIERS</span>
          <h2 className="hcp-title">Culinary Growth Plans</h2>

          {/* Dynamic Tabs */}
          <div className="hcp-tabs">
            {cycles.map((cycle) => (
              <button
                key={cycle.cycleId}
                className={`hcp-tab-btn ${
                  activeCycleKey === cycle.cycleKey ? "is-active" : ""
                }`}
                onClick={() => setActiveCycleKey(cycle.cycleKey)}
              >
                {cycle.displayName}
              </button>
            ))}
          </div>
        </div>

        {/* Plan Cards Grid */}
        <div className="hcp-grid">
          {plans.map((plan) => {
            const currentPrice = getPriceForPlan(plan, activeCycleKey);
            const isRecommended = plan.isRecommended;

            // Create a Set of included feature IDs for fast lookup
            const includedFeatureIds = new Set(
              plan.features.map((f) => f.featureId),
            );

            return (
              <div
                className={`hcp-card-wrapper ${isRecommended ? "is-recommended" : ""}`}
                key={plan.planId}
              >
                <div className="hcp-card">
                  {/* Floating Badge for Recommended Plan */}
                  {isRecommended && (
                    <div className="hcp-badge">RECOMMENDED</div>
                  )}

                  <div className="hcp-card__head">
                    <h3 className="hcp-plan-name">{plan.name}</h3>
                    <p className="hcp-plan-desc">{plan.tagLine}</p>
                  </div>

                  <div className="hcp-price-box">
                    <span className="hcp-currency">£</span>
                    <span className="hcp-amount">{currentPrice}</span>
                    <span className="hcp-cycle">
                      /{activeCycleKey?.toLowerCase()}
                    </span>
                  </div>

                  <ul className="hcp-features-list">
                    {/* 1. Included Features (Normal Style) - LOGIC UNCHANGED */}
                    {plan.features.map((feat) => (
                      <li
                        key={feat.featureId}
                        className="hcp-feature-item included"
                      >
                        {/* Left Icon */}
                        <svg
                          className="hcp-icon-check"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>

                        {/* Right Side: Text + Tooltip/List */}
                        <div className="hcp-feature-content">
                          <span>{getFeatureText(feat)}</span>

                          {feat.typeHint === "list" ? (
                            renderAdditionalFeatureList(feat.featureDetails)
                          ) : (
                            <FeatureTooltip details={feat.featureDetails} />
                          )}
                        </div>
                      </li>
                    ))}

                    {/* 2. Excluded Features (Strikethrough Style) at the end - LOGIC UNCHANGED */}
                    {allFeatures
                      .filter(
                        (globalFeat) =>
                          !includedFeatureIds.has(globalFeat.featureID),
                      )
                      .map((globalFeat) => (
                        <li
                          key={`global-${globalFeat.featureID}`}
                          className="hcp-feature-item excluded"
                        >
                          <span className="hcp-icon-cross">×</span>
                          <span>{globalFeat.displayName}</span>
                        </li>
                      ))}
                  </ul>

                  <button
                    className={`hcp-cta-btn ${isRecommended ? "hcp-btn-primary" : "hcp-btn-outline"}`}
                  >
                    SELECT PLAN
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomeChefPricing;
